import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Clock, CheckCircle, Circle, Users, ChevronRight, RefreshCw } from 'lucide-react'

import api from '../../services/api'

// ── Types & constants ──────────────────────────────────────────────────────────

type WorkerStatus = 'red' | 'amber' | 'green' | 'grey'

interface TeamWorker {
  id: string
  first_name: string
  last_name: string
  job_title: string
  department: string
  onboarding_id: string | null
  onboarding_status: string | null
  start_date: string | null
  template_name: string | null
  total_items: number
  completed_items: number
  blocking_incomplete: number
  overdue_items: number
  due_soon_items: number
}

function workerStatus(w: TeamWorker): WorkerStatus {
  if (!w.onboarding_id) return 'grey'
  if (w.blocking_incomplete > 0 || w.overdue_items > 0) return 'red'
  if (w.due_soon_items > 0) return 'amber'
  if (w.completed_items === w.total_items && w.total_items > 0) return 'green'
  return 'amber'
}

const STATUS_META: Record<WorkerStatus, { label: string; colour: string; bg: string; border: string; icon: any }> = {
  red:   { label: 'Action Required', colour: 'var(--red)',       bg: 'var(--red-light)',    border: 'var(--red)',    icon: AlertTriangle },
  amber: { label: 'In Progress',     colour: 'var(--amber)',     bg: 'var(--amber-light)',  border: 'var(--amber)',  icon: Clock },
  green: { label: 'Fully Onboarded', colour: 'var(--green)',     bg: 'var(--green-light)',  border: 'var(--green)',  icon: CheckCircle },
  grey:  { label: 'Not Started',     colour: 'var(--text-muted)',bg: 'var(--bg)',           border: 'var(--border)', icon: Circle },
}

const FILTER_OPTIONS: { value: WorkerStatus | 'all'; label: string }[] = [
  { value: 'all',   label: 'All Workers' },
  { value: 'red',   label: 'Action Required' },
  { value: 'amber', label: 'In Progress' },
  { value: 'green', label: 'Fully Onboarded' },
  { value: 'grey',  label: 'Not Started' },
]

function pct(done: number, total: number) {
  if (!total) return 0
  return Math.round((done / total) * 100)
}

// ── Worker card ────────────────────────────────────────────────────────────────

function WorkerCard({ w }: { w: TeamWorker }) {
  const status = workerStatus(w)
  const meta = STATUS_META[status]
  const Icon = meta.icon
  const progress = pct(w.completed_items, w.total_items)

  return (
    <Link
      to={`/workforce/${w.id}/onboarding`}
      style={{
        display: 'block', borderRadius: 12, textDecoration: 'none',
        border: `2px solid ${meta.border}`,
        background: meta.bg,
        padding: 16,
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-md)' }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = 'none' }}
    >
      {/* Avatar + status */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: meta.colour, color: 'white', fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {w.first_name[0]}{w.last_name[0]}
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', margin: 0 }}>{w.first_name} {w.last_name}</p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{w.job_title || w.department || '—'}</p>
          </div>
        </div>
        <Icon size={16} style={{ color: meta.colour, flexShrink: 0, marginTop: 2 }} />
      </div>

      {/* Status label */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: meta.colour, textTransform: 'uppercase', letterSpacing: 0.5 }}>{meta.label}</span>
        {w.onboarding_id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{progress}%</span>}
      </div>

      {/* Progress bar */}
      {w.onboarding_id ? (
        <>
          <div style={{ height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', width: `${progress}%`, background: meta.colour, borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
          {/* Detail flags */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {w.blocking_incomplete > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)' }}>
                ⚠ {w.blocking_incomplete} blocking item{w.blocking_incomplete !== 1 ? 's' : ''} incomplete
              </span>
            )}
            {w.overdue_items > 0 && (
              <span style={{ fontSize: 11, color: 'var(--red)' }}>
                {w.overdue_items} item{w.overdue_items !== 1 ? 's' : ''} overdue
              </span>
            )}
            {w.due_soon_items > 0 && w.overdue_items === 0 && (
              <span style={{ fontSize: 11, color: 'var(--amber)' }}>
                {w.due_soon_items} due this week
              </span>
            )}
            {status === 'green' && (
              <span style={{ fontSize: 11, color: 'var(--green)' }}>All {w.total_items} items complete</span>
            )}
            {w.template_name && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{w.template_name}</span>}
          </div>
        </>
      ) : (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No onboarding assigned</p>
      )}

      {/* View link */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 10, fontSize: 12, fontWeight: 600, color: meta.colour }}>
        View checklist <ChevronRight size={13} />
      </div>
    </Link>
  )
}

// ── Summary bar ────────────────────────────────────────────────────────────────

function SummaryBar({ workers }: { workers: TeamWorker[] }) {
  const counts = {
    red:   workers.filter(w => workerStatus(w) === 'red').length,
    amber: workers.filter(w => workerStatus(w) === 'amber').length,
    green: workers.filter(w => workerStatus(w) === 'green').length,
    grey:  workers.filter(w => workerStatus(w) === 'grey').length,
  }
  const total = workers.length

  return (
    <div className="card p-5">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {(Object.entries(counts) as [WorkerStatus, number][]).map(([status, count]) => {
          const meta = STATUS_META[status]
          const Icon = meta.icon
          return (
            <div key={status} style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
                <Icon size={16} style={{ color: meta.colour }} />
                <span style={{ fontSize: 26, fontWeight: 800, color: meta.colour }}>{count}</span>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{meta.label}</p>
              {total > 0 && <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: '2px 0 0' }}>{Math.round(count / total * 100)}%</p>}
            </div>
          )
        })}
      </div>

      {/* Stacked bar */}
      {total > 0 && (
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginTop: 14, gap: 1 }}>
          {(Object.entries(counts) as [WorkerStatus, number][]).map(([status, count]) =>
            count > 0 ? (
              <div key={status} style={{ flex: count, background: STATUS_META[status].colour, transition: 'flex 0.4s' }} />
            ) : null
          )}
        </div>
      )}
    </div>
  )
}

// ── Department grouping ────────────────────────────────────────────────────────

function DeptGroup({ dept, workers }: { dept: string; workers: TeamWorker[] }) {
  const [open, setOpen] = useState(true)
  const red = workers.filter(w => workerStatus(w) === 'red').length

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border)', marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{dept}</span>
          <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 999, background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{workers.length}</span>
          {red > 0 && <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 999, background: 'var(--red-light)', color: 'var(--red)', fontWeight: 700 }}>{red} need attention</span>}
        </div>
        <ChevronRight size={15} style={{ color: 'var(--text-muted)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 20 }}>
          {workers.map(w => <WorkerCard key={w.id} w={w} />)}
        </div>
      )}
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function TeamView() {
  const [workers, setWorkers] = useState<TeamWorker[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<WorkerStatus | 'all'>('all')
  const [groupByDept, setGroupByDept] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(new Date())

  const load = () => {
    setLoading(true)
    api.get('/onboarding/team').then(r => {
      setWorkers(r.data.data)
      setLastRefresh(new Date())
    }).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const filtered = filter === 'all' ? workers : workers.filter(w => workerStatus(w) === filter)

  // Group by department
  const departments: Record<string, TeamWorker[]> = {}
  filtered.forEach(w => {
    const dept = w.department || 'No Department'
    if (!departments[dept]) departments[dept] = []
    departments[dept].push(w)
  })

  const hasRed = workers.some(w => workerStatus(w) === 'red')

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Team Onboarding View</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Supervisor overview · {workers.length} worker{workers.length !== 1 ? 's' : ''} · Last updated {lastRefresh.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setGroupByDept(g => !g)}
            className="btn btn-secondary text-sm px-3 py-1.5"
            style={{ minHeight: 'auto', background: groupByDept ? 'var(--accent-light)' : undefined, color: groupByDept ? 'var(--accent)' : undefined }}
          >
            {groupByDept ? 'Grid view' : 'By department'}
          </button>
          <button onClick={load} className="btn btn-secondary p-2" style={{ minHeight: 'auto' }} title="Refresh">
            <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          <Link to="/workforce" className="btn btn-secondary text-sm flex items-center gap-1.5">
            <Users size={14} /> All Workers
          </Link>
        </div>
      </div>

      {/* Alert banner if any red workers */}
      {!loading && hasRed && (
        <div style={{ padding: '10px 16px', borderRadius: 10, background: 'var(--red-light)', border: '1px solid var(--red)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={16} style={{ color: 'var(--red)', flexShrink: 0 }} />
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', margin: 0 }}>
            {workers.filter(w => workerStatus(w) === 'red').length} worker{workers.filter(w => workerStatus(w) === 'red').length !== 1 ? 's' : ''} have blocking items incomplete or overdue items — they cannot be assigned to work until resolved.
          </p>
        </div>
      )}

      {/* Summary stats */}
      {!loading && workers.length > 0 && <SummaryBar workers={workers} />}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {FILTER_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            style={{
              padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: filter === opt.value ? 'var(--accent)' : 'var(--bg)',
              color: filter === opt.value ? 'white' : 'var(--text-secondary)',
              border: `1px solid ${filter === opt.value ? 'var(--accent)' : 'var(--border)'}`,
              transition: 'all 0.15s',
            }}
          >
            {opt.label}
            {opt.value !== 'all' && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                {workers.filter(w => workerStatus(w) === opt.value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading team…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <Users size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold" style={{ color: 'var(--text)' }}>
            {filter === 'all' ? 'No workers in the system yet' : `No workers with "${FILTER_OPTIONS.find(o => o.value === filter)?.label}" status`}
          </p>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="btn btn-secondary mt-3 text-sm">Show all workers</button>
          )}
        </div>
      ) : groupByDept ? (
        <div>
          {Object.entries(departments).sort(([a], [b]) => a.localeCompare(b)).map(([dept, deptWorkers]) => (
            <DeptGroup key={dept} dept={dept} workers={deptWorkers} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {/* Sort: red first, then amber, then grey, then green */}
          {[...filtered].sort((a, b) => {
            const order = { red: 0, amber: 1, grey: 2, green: 3 }
            return order[workerStatus(a)] - order[workerStatus(b)]
          }).map(w => <WorkerCard key={w.id} w={w} />)}
        </div>
      )}

      {/* Legend */}
      {!loading && workers.length > 0 && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {(Object.entries(STATUS_META) as [WorkerStatus, typeof STATUS_META[WorkerStatus]][]).map(([status, meta]) => {
            const Icon = meta.icon
            return (
              <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
                <Icon size={13} style={{ color: meta.colour }} />
                <span style={{ fontWeight: 600, color: meta.colour }}>{meta.label}</span>
                {status === 'red' && <span>— blocking items incomplete or overdue</span>}
                {status === 'amber' && <span>— items due soon or in progress</span>}
                {status === 'green' && <span>— all items complete</span>}
                {status === 'grey' && <span>— no onboarding assigned</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
